import { useCallback, useEffect, useMemo, useState } from 'react';
import { AssetValidationTabProps } from './index';
import { AssetIssues } from 'types/room';
import { updateLongLat, updateSubmitAsset } from 'services/locations';
import { differenceBy, compact } from 'lodash';
import { FormGenerateReport } from 'app/components/RoomPage/modalGenReport';
import { SizeOptionParams } from '../AssetModel/hook';
import {
  buildCreateReportPayload,
  buildSingleAssetReportPayload,
  resolveReportRoomId,
  submitReportGeneration
} from 'utils/reportGeneration';
import { useLoading } from 'contexts/loading';
import { useFlash } from 'contexts/flash';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { TypeEditAssetForm } from '../../elements/EditAssetForm';
import { useFetchAssetType } from 'hooks/useFetchAssetType';
import { useGetIssues } from 'hooks/useGetOptionIssues';
import { useGetOptionAssetIssues } from 'hooks/useGetOptionAssetIssues';
import { useInfoRoom } from 'hooks/useInfoRoom';
import { useFetchDocumentationWeb } from 'hooks/useGetDocuments';
import { useGetDocumentOption } from 'hooks/useGetDocumentOption';
import { uploadDocumentsOption } from 'services/inspections';
import { useGetDocumentGenerate } from 'hooks/useGetDocumentGenerate';
import { useFetchAssetModel } from 'hooks/useFetchAssetModel';
import {
  appendDynamicOptionForPayload,
  appendDynamicSingleForPayload,
  resolveInspectionLocationPatchIds
} from 'utils/convertData';
import {
  useFetchManufactures,
  useInsertOptionManufacturer,
  useManufactureStore
} from 'store/manufacture';
import { useFetchTriggerSubTypes } from 'store/triggerSubType';
import { useRoomContext } from 'contexts/RoomContext';
import { useNavigate } from 'react-router-dom';

export const useInjection = ({
  initSearchText,
  setDataAssetValidation,
  dataAssetValidation,
  refreshDataValidation,
  expandedSubmittedAssetId,
  onExpandedSubmittedAssetChange
}: Partial<AssetValidationTabProps>) => {
  const { setLoading } = useLoading();
  const { setFlash } = useFlash();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState<string>(initSearchText || '');
  useEffect(() => {
    setSearchText(initSearchText || '');
  }, [initSearchText]);
  const [rowActive, setRowActive] = useState<AssetIssues | undefined>(undefined);
  const [idActiveRow, setIdActiveRow] = useState<number | undefined>(undefined);
  const [previewGenerate, setPreviewGenerate] = useState<AssetIssues | undefined>(undefined);

  // generate report
  const [generateReport, setGenerateReport] = useState<AssetIssues | undefined>(undefined);
  // Option assess Issues

  const { manufacturers } = useManufactureStore();

  const { room } = useInfoRoom(generateReport?.room.id);
  const { users } = useRoomContext();
  const { optionAssetSelect } = useGetOptionAssetIssues(generateReport?.room.id);
  const { documentOptions, modelFiles, setDocumentOptions } = useGetDocumentOption(rowActive);

  const {
    data: optionSelectDocument,
    handleFetchMoreDocumentation,
    hasMore: hasMoreSelectDocument,
    asyncAddDocument
  } = useFetchDocumentationWeb();

  const {
    optionIssues,
    hasMoreOptionIssues,
    fetchMoreIssues: handleFetchMoreIssues
  } = useGetIssues(generateReport?.room.id);
  const {
    optionAsset: optionTypes,
    hasMoreOptionAsset: hasMoreOptionType,
    handleFetchMoreOptionAsset: handleFetchMoreOptionType,
    getOptionAsset
  } = useFetchAssetType();

  const { optionModel, hasMoreOptionModel, handleFetchMoreOptionModel, getOptionModel } =
    useFetchAssetModel(rowActive?.model);

  const fetchManufacturers = useFetchManufactures();
  const insertOptionManufacturer = useInsertOptionManufacturer();
  const fetchTriggerSubTypes = useFetchTriggerSubTypes();

  useEffect(() => {
    getOptionModel({ page: 1, size: SizeOptionParams });
  }, [getOptionModel]);

  useEffect(() => {
    const fetchAll = async () => {
      const paramsConvert = {
        page: 1,
        size: SizeOptionParams
      };
      await Promise.all([getOptionAsset(paramsConvert)]);
    };
    fetchAll();
  }, [getOptionAsset]);

  useEffect(() => {
    fetchManufacturers();
  }, [fetchManufacturers]);

  // Needed to resolve triggerSubTypeId ids to display names in EditAssetForm
  useEffect(() => {
    fetchTriggerSubTypes();
  }, [fetchTriggerSubTypes]);

  useEffect(() => {
    if (expandedSubmittedAssetId == null) {
      setIdActiveRow(undefined);
      setRowActive(undefined);
      return;
    }
    const list = dataAssetValidation ?? [];
    const idx = list.findIndex((a) => a.id === expandedSubmittedAssetId);
    if (idx >= 0) {
      const row = list[idx];
      setIdActiveRow(idx);
      setRowActive(row);
      if (row?.model?.manufacturer?.id)
        insertOptionManufacturer({
          label: row.model.manufacturer.name,
          value: row.model.manufacturer.id.toString()
        });
    } else {
      setIdActiveRow(undefined);
      setRowActive(undefined);
    }
  }, [expandedSubmittedAssetId, dataAssetValidation, insertOptionManufacturer]);

  const onCloseModalGenerateReport = useCallback(() => {
    setGenerateReport(undefined);
  }, []);
  const onSubmitModalGenerateReport = useCallback(
    async (value: FormGenerateReport) => {
      onCloseModalGenerateReport();
      setLoading(true);
      try {
        const roomLocationId = resolveReportRoomId(generateReport?.room?.id, room?.roomId);
        const convertPayload = buildCreateReportPayload(value, roomLocationId);
        await submitReportGeneration(convertPayload, setFlash);
      } finally {
        setLoading(false);
      }
    },
    [generateReport?.room?.id, onCloseModalGenerateReport, room?.roomId, setFlash, setLoading]
  );
  const onGenerateSingleAsset = useCallback(
    async (asset: AssetIssues) => {
      setLoading(true);
      try {
        const ok = await submitReportGeneration(buildSingleAssetReportPayload(asset), setFlash);
        if (ok) {
          navigate(
            `/clients/${asset.room.client.clientName}/${asset.room.client.id}/location/${asset.room.id}/room?tab=documentation`
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [setFlash, setLoading, navigate]
  );

  const handleUpdateAsset = useCallback(
    async (value: TypeEditAssetForm) => {
      if (!rowActive?.submitId || !rowActive?.id) return;
      const removedDocuments = differenceBy(documentOptions, value.documentOptions || [], 'id');
      const removedIds = removedDocuments.map((doc) => doc.id);
      if (value?.uploadedFiles?.length || removedIds?.length) {
        const data = (await uploadDocumentsOption({
          submitId: rowActive?.submitId,
          submittedAssets: rowActive?.id,
          payload: {
            files: value?.uploadedFiles,
            deleteDocumentIds: removedIds
          }
        })) as { submittedAsset: AssetIssues };
        setDocumentOptions(data.submittedAsset.documentations);
      }
      const {
        clientId,
        roomId,
        ok: patchLocationOk
      } = resolveInspectionLocationPatchIds(rowActive);
      const patchRequests: Promise<unknown>[] = [
        updateSubmitAsset({
          submitId: rowActive?.submitId,
          submittedAssetId: rowActive?.id,
          payload: {
            name: value.assetName,
            typeId: Number(value.assetType),
            modelId: value.model === 'OTHER' ? 0 : Number(value.model),
            model: value?.modelName || '',
            criticalityKey: value.criticalityKey,
            assetCustomId: value?.assetCustomId || '',
            assetCustomId2: value?.assetCustomId2 || '',
            lastMaintenance: value?.lastMaintenance || null,
            manufacturerId:
              value?.manufacturerId && value.manufacturerId !== 'OTHER'
                ? Number(value.manufacturerId)
                : undefined,
            manufacturer: value?.manufacturer,
            validated: value.validated === 'Yes',
            note: value.note,
            otherNote: value.otherNote,
            entries: JSON.stringify(value.entries),
            options: JSON.stringify(appendDynamicOptionForPayload(value)),
            singleFields: JSON.stringify(appendDynamicSingleForPayload(value)),
            oemAttributes: value.oemAttributes ? JSON.stringify(value.oemAttributes) : undefined,
            maintenanceEnabled: value.maintenanceEnabled, // add
            assetDevices: value.maintenanceEnabled // add
              ? (value.assetDevices || []).map((d) => ({
                  id: d.id,
                  maintenanceTriggerType: d.maintenanceTriggerType,
                  frequencyType: d.frequencyType,
                  maintenanceFrequency:
                    d.frequencyType === 'INTERVAL' ? d.maintenanceFrequency : null,
                  minMaintenanceFrequency:
                    d.frequencyType === 'RANGE' ? d.minMaintenanceFrequency : null,
                  maxMaintenanceFrequency:
                    d.frequencyType === 'RANGE' ? d.maxMaintenanceFrequency : null,
                  notificationWindow: d.notificationWindow,
                  currentReadingValue: d.currentReadingValue,
                  counterSource: d.counterSource,
                  iotDeviceId: d.iotDeviceId,
                  iotDeviceName: d.iotDeviceName
                }))
              : []
          },
          newPhotos: value.newPhotos,
          archivePhotoIds: value.archivePhotoIds
        })
      ];
      if (patchLocationOk) {
        patchRequests.push(
          updateLongLat({
            submitId: rowActive?.submitId,
            lat: value?.latitude || '',
            long: value?.longitude || '',
            clientId,
            roomId,
            date: rowActive?.updatedAt
          })
        );
      }
      const [updateResultRaw] = await Promise.all(patchRequests);
      const updateResult = updateResultRaw as { submittedAsset?: AssetIssues };
      const type = optionTypes.find((item) => item.value === value.assetType);
      const updatedPhotos = updateResult?.submittedAsset?.photos || rowActive.photos;

      const newDataAssetValidation = compact(
        dataAssetValidation?.map((item) => {
          if (item.id === rowActive.id) {
            return {
              ...rowActive,
              name: value.assetName,
              validated: value.validated === 'Yes',
              photos: updatedPhotos,
              type: {
                id: Number(type?.value),
                name: type?.label || ''
              }
            };
          }
          return item;
        })
      );
      setDataAssetValidation && setDataAssetValidation(newDataAssetValidation);

      const data = await refreshDataValidation?.();
      const stillExists = data?.find((item) => item.id === rowActive.id);
      // Keep expanded row aligned with this asset: list order can change after refresh/sort.
      const newRowIndex = data?.findIndex((item) => item.id === rowActive.id);

      if (stillExists) {
        if (stillExists?.model?.manufacturer?.id)
          insertOptionManufacturer({
            label: stillExists?.model?.manufacturer.name,
            value: stillExists?.model?.manufacturer?.id?.toString()
          });
        setRowActive({ ...stillExists, photos: updatedPhotos });
        if (newRowIndex !== undefined && newRowIndex >= 0) {
          setIdActiveRow(newRowIndex);
        }
      } else {
        setRowActive(undefined);
        setIdActiveRow(undefined);
        onExpandedSubmittedAssetChange?.(undefined);
      }

      setFlash({ message: 'Action completed successfully!', type: 'success' });
    },
    [
      rowActive,
      documentOptions,
      optionTypes,
      dataAssetValidation,
      setDataAssetValidation,
      refreshDataValidation,
      setFlash,
      setDocumentOptions,
      insertOptionManufacturer,
      onExpandedSubmittedAssetChange
    ]
  );
  const { asyncCallback: asyncHandleUpdateAsset } = useAsyncCallback(handleUpdateAsset, []);
  const { documentGenerate } = useGetDocumentGenerate({ roomId: Number(room?.roomId) });

  return {
    searchText,
    setSearchText,
    rowActive,
    setRowActive,
    idActiveRow,
    setIdActiveRow,
    setPreviewGenerate,
    room,
    previewGenerate,
    generateReport,
    setGenerateReport,
    optionIssues,
    handleFetchMoreIssues,
    hasMoreIssues: hasMoreOptionIssues,
    optionAssetSelect,
    onCloseModalGenerateReport,
    onSubmitModalGenerateReport,
    users,
    handleUpdateAsset: asyncHandleUpdateAsset,
    optionTypes,
    hasMoreOptionType,
    handleFetchMoreOptionType,
    optionSelectDocument,
    handleFetchMoreDocumentation,
    hasMoreSelectDocument,
    asyncAddDocument,
    documentOptions,
    documentGenerate,
    modelFiles,
    optionModel,
    hasMoreOptionModel,
    handleFetchMoreOptionModel,
    manufacturers,
    onGenerateSingleAsset,
    selectedCriticalityOptions: useMemo(() => {
      const options = Array.from({ length: 5 }).map((_, index) => ({
        label: `${index + 1}`,
        value: `${index + 1}`
      }));
      options.unshift({ label: 'All', value: '0' });
      return options;
    }, [])
  };
};
